import { prepareHeaders } from '@/lib/cookie'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { ClickupConfigResponse } from './datascope'
import { BASE_URL } from '@/constant'

export interface ClickupTokenResponse {
  message: string
  data: string
}

export interface SyncScheduleRequest {
  sync_frequency: number
  scheduled_hour: number
}

export interface ScheduleData {
  scheduled_hour: number
  sync_frequency: number
  next_run_at: string
}

export interface ScheduleResponse {
  message: string
  data: ScheduleData
}

export interface OpenaiConfigResponse {
  message: string
  data: string
}

export interface UpdateOpenaiRequest {
  open_ai: string
}

export const configurationApi = createApi({
  reducerPath: 'configurationApi',
  tagTypes: ['Configuration'],
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: prepareHeaders,
  }),
  endpoints: builder => ({
    getClickupConfig: builder.query<ClickupConfigResponse, void>({
      query: () => '/configurations',
      providesTags: ['Configuration'],
    }),
    getClickupToken: builder.query<ClickupTokenResponse, void>({
      query: () => '/configurations/token',
    }),

    updateConfiguration: builder.mutation({
      query: body => ({
        url: '/configurations',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Configuration'],
    }),
    updateWorkspaces: builder.mutation({
      query: body => ({
        url: '/configurations/workspaces',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Configuration'],
    }),
    updateSpaces: builder.mutation({
      query: body => ({
        url: '/configurations/spaces',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Configuration'],
    }),
    updateSyncSchedule: builder.mutation<
      { message: string },
      SyncScheduleRequest
    >({
      query: body => ({
        url: '/configurations/schedule',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Configuration'],
    }),
    getSchedule: builder.query<ScheduleResponse, void>({
      query: () => '/configurations/schedule',
      providesTags: ['Configuration'],
    }),
    syncClickupTimesheet: builder.query<
      { message: string },
      { startDate: number; endDate: number }
    >({
      query: ({ startDate, endDate }) => ({
        url: `/configurations/sync`,
        params: { startDate, endDate },
      }),
    }),
    getOpenaiConfig: builder.query<OpenaiConfigResponse, void>({
      query: () => '/configurations/openai',
      providesTags: ['Configuration'],
    }),
    updateOpenai: builder.mutation<{ message: string }, UpdateOpenaiRequest>({
      query: body => ({
        url: '/configurations/openai',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Configuration'],
    }),
  }),
})

export const {
  useGetClickupConfigQuery,
  useGetClickupTokenQuery,
  useLazyGetClickupTokenQuery,
  useUpdateConfigurationMutation,
  useUpdateWorkspacesMutation,
  useUpdateSpacesMutation,
  useUpdateSyncScheduleMutation,
  useGetScheduleQuery,
  useLazySyncClickupTimesheetQuery,
  useGetOpenaiConfigQuery,
  useLazyGetOpenaiConfigQuery,
  useUpdateOpenaiMutation,
} = configurationApi
